/**
 * Pure state machine for the persistent Adventure CTA panel.
 *
 * Derives what the lobby's "Begin Your Adventure" surface should show from
 * store facts. No I/O, no store imports, unit-testable in isolation.
 *
 * Precedence (first match wins):
 *   hidden (no character) > pending > resume (active session) >
 *   waiting-leader (member, not leader) > pick-module (multiplayer leader,
 *   no module) > start (leader with module) > quick-start (no party, or
 *   solo party without a module).
 *
 * A completed module still derives `start`: replay is allowed (matches
 * the backend's StartOnly semantics; revisit when a second module ships).
 * The tappable launch states (`start`, `quick-start`) carry `returning`,
 * true once the party has any play history, so the button reads
 * "Continue Your Adventure" instead of "Begin Your Adventure".
 */

export type AdventureCtaState =
  /** No character yet, the panel doesn't render. */
  | { kind: 'hidden' }
  /** A quick-start request is in flight; button shows a spinner. */
  | { kind: 'pending' }
  /** Party has an active session; tapping resumes it. */
  | { kind: 'resume' }
  /** In a party led by someone else; passive hint, no button. */
  | { kind: 'waiting-leader'; leaderName: string | null }
  /** Multiplayer leader without a module; steer to party chat, no button. */
  | { kind: 'pick-module' }
  /** Leader with a module selected; tapping starts. `returning` picks the label. */
  | { kind: 'start'; returning: boolean }
  /** No party (or solo party without module); tapping quick-starts. `returning` picks the label. */
  | { kind: 'quick-start'; returning: boolean };

export interface AdventureCtaPartyFacts {
  /** The viewer's character leads this party. */
  isLeader: boolean;
  /** Display name of the leader (for the waiting hint). */
  leaderName: string | null;
  memberCount: number;
  hasModule: boolean;
  hasActiveSession: boolean;
  /** Party has prior play history (a completed module or a past session end). */
  hasPlayedBefore: boolean;
}

export interface AdventureCtaInputs {
  hasCharacter: boolean;
  quickStartPending: boolean;
  party: AdventureCtaPartyFacts | null;
}

export function deriveAdventureCta(inputs: AdventureCtaInputs): AdventureCtaState {
  if (!inputs.hasCharacter) {
    return { kind: 'hidden' };
  }
  if (inputs.quickStartPending) {
    return { kind: 'pending' };
  }

  const party = inputs.party;
  if (!party) {
    return { kind: 'quick-start', returning: false };
  }
  if (party.hasActiveSession) {
    return { kind: 'resume' };
  }
  if (!party.isLeader) {
    return { kind: 'waiting-leader', leaderName: party.leaderName };
  }
  if (!party.hasModule && party.memberCount > 1) {
    return { kind: 'pick-module' };
  }
  if (party.hasModule) {
    return { kind: 'start', returning: party.hasPlayedBefore };
  }
  return { kind: 'quick-start', returning: party.hasPlayedBefore };
}
